import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { MicOff } from '@mui/icons-material';
import { Participant } from '../types';
import { VIDEO_GRID_LAYOUT } from '../constants';
import { VideoControls } from './VideoControls';

// Error boundary for VideoGrid
class VideoGridErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('VideoGrid Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          flexGrow={1}
          overflow="auto"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Box
            width={VIDEO_GRID_LAYOUT.localVideoWidth}
            position="relative"
            sx={{
              aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio,
              maxWidth: VIDEO_GRID_LAYOUT.maxWidth
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              sx={{
                bgcolor: '#ff444420',
                borderRadius: '8px',
                border: '2px solid #ff4444',
              }}
            >
              <Typography color="error">
                Video component error: {this.state.error?.message || 'Unknown error'}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

interface VideoGridProps {
  participants: Participant[];
  localParticipant: any;
  videoRefs: {
    local: React.RefObject<HTMLVideoElement>;
    remote: React.MutableRefObject<{[key: string]: HTMLVideoElement | null}>;
  };
  onToggleVideo?: () => Promise<void>;
  onToggleAudio?: () => Promise<void>;
}

const VideoGridComponent: React.FC<VideoGridProps> = ({
  participants,
  localParticipant,
  videoRefs,
  onToggleVideo,
  onToggleAudio
}) => {
  const theme = useTheme();
  // Use hardcoded strings to avoid i18next issues
  const t = (key: string) => {
    const translations: { [key: string]: string } = {
      'You': 'You',
      'Connecting...': 'Connecting...'
    };
    return translations[key] || key;
  };

  console.log('VideoGrid received props:', {
    participants: participants?.length || 0,
    localParticipant: !!localParticipant,
    videoRefs: !!videoRefs,
    videoRefsLocal: !!videoRefs?.local,
    videoRefsRemote: !!videoRefs?.remote,
    videoRefsRemoteCurrent: !!videoRefs?.remote?.current
  });

  try {
    // Safety check for videoRefs
    if (!videoRefs || !videoRefs.local || !videoRefs.remote) {
      console.error('VideoGrid: videoRefs, videoRefs.local, or videoRefs.remote is undefined');
      return (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          flexGrow={1}
          overflow="auto"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Box
            width={VIDEO_GRID_LAYOUT.localVideoWidth}
            position="relative"
            sx={{
              aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio,
              maxWidth: VIDEO_GRID_LAYOUT.maxWidth
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              sx={{
                bgcolor: '#1f2535',
                borderRadius: '8px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography color="textSecondary">
                Video refs not initialized
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    // Additional safety check for remote.current
    if (!videoRefs.remote.current) {
      console.error('VideoGrid: videoRefs.remote.current is undefined, initializing...');
      try {
        videoRefs.remote.current = {};
      } catch (error) {
        console.error('Error initializing remote.current:', error);
        return (
          <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          flexGrow={1}
          overflow="auto"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Box
            width={VIDEO_GRID_LAYOUT.localVideoWidth}
            position="relative"
            sx={{
              aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio,
              maxWidth: VIDEO_GRID_LAYOUT.maxWidth
            }}
          >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                sx={{
                  bgcolor: '#ff444420',
                  borderRadius: '8px',
                  border: '2px solid #ff4444',
                }}
              >
                <Typography color="error">
                  Error initializing video refs
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      }
    }

  console.log('VideoGrid render:', {
    participantsCount: participants?.length || 0,
    localParticipant: !!localParticipant,
    localParticipantSid: localParticipant?.sid,
    videoRefsLocal: !!videoRefs?.local?.current
  });

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={2}
      overflow="auto"
      justifyContent="center" // Center the video containers
      alignItems="flex-start" // Align to top
      minHeight="fit-content" // Only take the space needed
    >
      {/* Local Participant Video - Always render the video element */}
      <Box
        width={VIDEO_GRID_LAYOUT.localVideoWidth}
        position="relative"
        sx={{
          aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio, // Force 16:9 aspect ratio
          maxHeight: VIDEO_GRID_LAYOUT.maxHeight, // Prevent video from being too tall
          maxWidth: VIDEO_GRID_LAYOUT.maxWidth, // Maximum width constraint
          '& video': {
            aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio, // Ensure video maintains 16:9
          }
        }}
      >
          <video
            ref={videoRefs.local}
            autoPlay
            muted
            playsInline
            controls={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover', // Fill the entire container, cropping if necessary
              borderRadius: '8px',
              backgroundColor: '#000',
              border: `2px solid ${theme.palette.divider}`, // Add border to make it visible
              aspectRatio: '16/9' // Force 16:9 aspect ratio
            }}
            onLoadedMetadata={() => {
              console.log('Local video metadata loaded');
              if (videoRefs?.local?.current) {
                console.log('Local video dimensions:', {
                  videoWidth: videoRefs.local.current.videoWidth,
                  videoHeight: videoRefs.local.current.videoHeight,
                  clientWidth: videoRefs.local.current.clientWidth,
                  clientHeight: videoRefs.local.current.clientHeight
                });
              }
            }}
            onCanPlay={() => {
              console.log('Local video can play');
            }}
            onLoadStart={() => {
              console.log('Local video load started');
            }}
            onError={(e) => {
              console.error('Local video error:', e);
            }}
          />
          {/* Debug info overlay */}
          <Box
            position="absolute"
            top={8}
            left={8}
            bgcolor="rgba(0,0,0,0.7)"
            color="white"
            px={1}
            py={0.5}
            borderRadius={1}
            fontSize="12px"
          >
            Debug: {localParticipant ? 'Connected' : 'No participant'} |
            Ref: {videoRefs?.local?.current ? 'Set' : 'Null'}
          </Box>
          <Box
            position="absolute"
            bottom={8}
            left={8}
            bgcolor="rgba(0,0,0,0.5)"
            color="white"
            px={1}
            borderRadius={1}
          >
            {localParticipant ? t('You') : t('Connecting...')}
            {localParticipant && !localParticipant.isMicrophoneEnabled && (
              <MicOff style={{ fontSize: 16, marginLeft: 4 }} />
            )}
          </Box>

          {/* Video Controls - positioned directly on the local video */}
          {onToggleVideo && onToggleAudio && (
            <VideoControls
              localParticipant={localParticipant}
              onToggleVideo={onToggleVideo}
              onToggleAudio={onToggleAudio}
            />
          )}
        </Box>

      {/* Remote Participants */}
      {(participants || []).filter(p => p && !p.isLocal).map(participant => (
        <Box
          key={participant.id}
          width={VIDEO_GRID_LAYOUT.remoteVideoWidth}
          position="relative"
          sx={{
            aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio, // Force 16:9 aspect ratio
            '& video': {
              aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio, // Ensure video maintains 16:9
            }
          }}
        >
          <video
            ref={(el) => {
              console.log('Remote video ref callback called:', {
                participantId: participant.id,
                hasElement: !!el,
                hasVideoRefs: !!videoRefs,
                hasVideoRefsRemote: !!videoRefs?.remote,
                hasVideoRefsRemoteCurrent: !!videoRefs?.remote?.current
              });

              if (el && videoRefs?.remote) {
                // Ensure remote.current is initialized
                if (!videoRefs.remote.current) {
                  console.log('Initializing remote.current object');
                  videoRefs.remote.current = {};
                }

                try {
                  videoRefs.remote.current[participant.id] = el;
                  console.log('Remote video ref assigned successfully:', {
                    participantId: participant.id,
                    hasElement: !!el,
                    hasRemoteRef: !!videoRefs.remote,
                    hasRemoteCurrent: !!videoRefs.remote.current
                  });
                } catch (error) {
                  console.error('Error assigning remote video ref:', error);
                }
              } else {
                console.log('Skipping remote video ref assignment:', {
                  hasElement: !!el,
                  hasVideoRefs: !!videoRefs,
                  hasVideoRefsRemote: !!videoRefs?.remote
                });
              }
            }}
            autoPlay
            playsInline
            data-participant-id={participant.id}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover', // Fill the entire container, cropping if necessary
              borderRadius: '8px',
              backgroundColor: '#000',
              aspectRatio: '16/9' // Force 16:9 aspect ratio
            }}
          />
          <Box
            position="absolute"
            bottom={8}
            left={8}
            bgcolor="rgba(0,0,0,0.5)"
            color="white"
            px={1}
            borderRadius={1}
          >
            {participant.name}
            {!participant.audioEnabled && (
              <MicOff style={{ fontSize: 16, marginLeft: 4 }} />
            )}
          </Box>
          {participant.isSpeaking && (
            <Box
              position="absolute"
              top={-2}
              left={-2}
              right={-2}
              bottom={-2}
              border="2px solid #4caf50"
              borderRadius="8px"
              sx={{ pointerEvents: 'none' }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
  } catch (error) {
    console.error('VideoGrid error:', error);
    return (
      <Box display="flex" flexWrap="wrap" gap={2} flexGrow={1} overflow="auto">
          <Box
            width={VIDEO_GRID_LAYOUT.localVideoWidth}
            position="relative"
            sx={{
              aspectRatio: VIDEO_GRID_LAYOUT.aspectRatio,
              maxWidth: VIDEO_GRID_LAYOUT.maxWidth
            }}
          >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            sx={{
              bgcolor: theme.palette.error.main + '20',
              borderRadius: '8px',
              border: `2px solid ${theme.palette.error.main}`,
            }}
          >
            <Typography color="error">
              Video component error: {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
};

// Export with error boundary wrapper
export const VideoGrid: React.FC<VideoGridProps> = (props) => {
  return (
    <VideoGridErrorBoundary>
      <VideoGridComponent {...props} />
    </VideoGridErrorBoundary>
  );
};
